/**
 * Поле для числа.
 *
 * Обычное поле с числом ведёт себя предсказуемо ровно до того момента, когда
 * человек хочет ввести другое число. Он стирает содержимое — а управляемое
 * поле тут же возвращает подставленное значение, и дописывать приходится к
 * нему: вместо «10» получается «110». Ровно так и было с шагом повторения,
 * где по умолчанию стоит единица.
 *
 * Поэтому пока поле правят, в нём живёт текст, а не число: пустое поле — это
 * незаконченный ввод, а не ноль. Наружу при этом уходит только осмысленное
 * число, а когда человек уходит с пустого поля, в нём появляется запасное
 * значение — форма не должна остаться без числа вовсе.
 */

import type { ChangeEvent, FocusEvent } from 'react';
import { useState } from 'react';

import { Input, type InputProps } from './Field';

interface NumberInputProps extends Omit<InputProps, 'value' | 'onChange' | 'type'> {
  value: number;
  onChange: (value: number) => void;
  /**
   * Что подставить, если поле оставили пустым.
   *
   * По умолчанию возвращается то значение, которое было до правки: человек
   * стёр и передумал — значит, ничего не менял.
   */
  fallback?: number;
}

export function NumberInput({ value, onChange, fallback, onBlur, ...rest }: NumberInputProps) {
  const [text, setText] = useState(String(value));

  /*
    Значение снаружи и текст внутри сверяются в рендере, а не эффектом: пока
    человек не правит поле, правда — за формой, и пришедшее извне число должно
    появиться сразу. Сравнение идёт по числу, а не по тексту: «07» и «7» — одно
    и то же, и переписывать набранное из-за этого незачем.
  */
  const [synced, setSynced] = useState(value);
  if (synced !== value) {
    setSynced(value);
    if (Number.parseFloat(text) !== value) setText(String(value));
  }

  const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
    const next = event.target.value;
    setText(next);

    const parsed = Number.parseFloat(next);
    if (!Number.isNaN(parsed)) {
      setSynced(parsed);
      onChange(parsed);
    }
  };

  const handleBlur = (event: FocusEvent<HTMLInputElement>) => {
    if (Number.isNaN(Number.parseFloat(text))) {
      const restored = fallback ?? value;
      setText(String(restored));
      setSynced(restored);
      onChange(restored);
    }

    onBlur?.(event);
  };

  return (
    <Input
      type="number"
      inputMode="numeric"
      value={text}
      onChange={handleChange}
      onBlur={handleBlur}
      {...rest}
    />
  );
}
